import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { BookOpen } from 'lucide-react';
import { journalAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import JournalEntryForm from './JournalEntryForm';

const EMPTY_FORM = {
  side: 'buy',
  entryPrice: '',
  size: '',
  stopLoss: '',
  takeProfit: '',
  strategy: '',
  thesis: '',
};

/**
 * Reusable one-click "Add to Journal" dialog.
 * Pre-fills symbol / entry price / side and posts to journalAPI.create.
 *
 * Form rendering is delegated to <JournalEntryForm /> to keep this container
 * focused on state, submit, and dialog chrome.
 */
const AddToJournalDialog = ({ open, onOpenChange, symbol, defaultEntryPrice, defaultSide = 'buy', source = 'screener' }) => {
  const { toast } = useToast();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    side: defaultSide,
    entryPrice: defaultEntryPrice ?? '',
  });

  useEffect(() => {
    // Refresh defaults whenever dialog re-opens with a new symbol
    if (open) {
      setForm((f) => ({
        ...f,
        entryPrice: defaultEntryPrice ?? f.entryPrice ?? '',
        side: defaultSide ?? f.side ?? 'buy',
      }));
    }
  }, [open, symbol, defaultEntryPrice, defaultSide]);

  const handleSave = async () => {
    if (!symbol) return;
    setSaving(true);
    try {
      const payload = {
        symbol: String(symbol).toUpperCase(),
        side: form.side,
        status: 'open',
        entryPrice: form.entryPrice ? Number(form.entryPrice) : undefined,
        size: form.size ? Number(form.size) : undefined,
        stopLoss: form.stopLoss ? Number(form.stopLoss) : undefined,
        takeProfit: form.takeProfit ? Number(form.takeProfit) : undefined,
        strategy: form.strategy || undefined,
        thesis: form.thesis || undefined,
        entryDate: new Date().toISOString(),
        tags: [`source:${source}`],
      };
      await journalAPI.create(payload);
      toast({ title: 'Added to journal', description: `${symbol} trade logged.` });
      onOpenChange(false);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not save to journal';
      toast({ title: 'Error', description: String(msg), variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="add-to-journal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BookOpen size={18} className="text-purple-600" />
            Add {symbol} to Trading Journal
          </DialogTitle>
          <DialogDescription>Log this trade idea. You can edit / close it later from Journal.</DialogDescription>
        </DialogHeader>

        <JournalEntryForm form={form} onChange={setForm} />

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="journal-cancel-btn">Cancel</Button>
          <Button
            className="bg-purple-600 hover:bg-purple-700"
            onClick={handleSave}
            disabled={saving || !symbol}
            data-testid="journal-save-btn"
          >
            {saving ? 'Saving…' : 'Save to Journal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddToJournalDialog;
